import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Clock, Play, Pause, ArrowsClockwise, CheckCircle, Calendar } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SchedulerPage = ({ user, setUser }) => {
  const [config, setConfig] = useState({
    enabled: false,
    schedule_hour: 6,
    schedule_minute: 0,
    days_to_import: 7,
    max_tickets: 50,
    site_key: "gardinshoppen"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [nextRun, setNextRun] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/scheduler/config`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setConfig({
          enabled: data.enabled || false,
          schedule_hour: data.schedule_hour || 6,
          schedule_minute: data.schedule_minute || 0,
          days_to_import: data.days_to_import || 7,
          max_tickets: data.max_tickets || 50,
          site_key: data.site_key || "gardinshoppen"
        });
        setNextRun(data.next_run);
      }
    } catch (error) {
      toast.error("Kunne ikke hente scheduler konfiguration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/scheduler/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchConfig(); // Refresh to get next_run
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke gemme konfiguration");
      }
    } catch (error) {
      toast.error("Der opstod en fejl");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const response = await fetch(`${API}/scheduler/run-now`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Import startet (Job ID: ${data.job_id})`);
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error("Kunne ikke starte import");
    } finally {
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="p-6 md:p-8 flex items-center justify-center">
          <ArrowsClockwise className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="scheduler-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Planlagt Import
          </h1>
          <p className="text-slate-600">Konfigurer automatisk import af Zendesk tickets</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Status Card */}
          <Card className="card-default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" weight="duotone" />
                Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Planlagt import</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {config.enabled ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                
                {config.enabled && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Kører dagligt kl.</span>
                    <span className="font-mono text-slate-900">
                      {String(config.schedule_hour).padStart(2, '0')}:{String(config.schedule_minute).padStart(2, '0')}
                    </span>
                  </div>
                )}
                
                {nextRun && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Næste kørsel</span>
                    <span className="text-slate-900">
                      {format(new Date(nextRun), "d. MMM 'kl.' HH:mm", { locale: da })}
                    </span>
                  </div>
                )}
                
                <Button 
                  onClick={runNow} 
                  disabled={runningNow}
                  className="w-full btn-primary"
                  data-testid="run-now-btn"
                >
                  {runningNow ? (
                    <><ArrowsClockwise className="w-4 h-4 mr-2 animate-spin" />Starter...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" weight="fill" />Kør import nu</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Card */}
          <Card className="card-default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" weight="duotone" />
                Konfiguration
              </CardTitle>
              <CardDescription>Indstil hvornår og hvordan import skal køre</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Aktiver planlagt import</Label>
                    <p className="text-xs text-slate-500">Kør automatisk dagligt</p>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                    data-testid="scheduler-toggle"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Select 
                      value={String(config.schedule_hour)} 
                      onValueChange={(v) => setConfig(prev => ({ ...prev, schedule_hour: parseInt(v) }))}
                    >
                      <SelectTrigger data-testid="hour-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {String(i).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Minut</Label>
                    <Select 
                      value={String(config.schedule_minute)} 
                      onValueChange={(v) => setConfig(prev => ({ ...prev, schedule_minute: parseInt(v) }))}
                    >
                      <SelectTrigger data-testid="minute-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map(m => (
                          <SelectItem key={m} value={String(m)}>
                            {String(m).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Dage at kigge tilbage</Label>
                  <Select 
                    value={String(config.days_to_import)} 
                    onValueChange={(v) => setConfig(prev => ({ ...prev, days_to_import: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 dag</SelectItem>
                      <SelectItem value="3">3 dage</SelectItem>
                      <SelectItem value="7">7 dage</SelectItem>
                      <SelectItem value="14">14 dage</SelectItem>
                      <SelectItem value="30">30 dage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Maks antal tickets</Label>
                  <Select 
                    value={String(config.max_tickets)} 
                    onValueChange={(v) => setConfig(prev => ({ ...prev, max_tickets: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="max-tickets-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 tickets</SelectItem>
                      <SelectItem value="50">50 tickets</SelectItem>
                      <SelectItem value="100">100 tickets</SelectItem>
                      <SelectItem value="200">200 tickets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={saveConfig} 
                  disabled={saving}
                  className="w-full"
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <><ArrowsClockwise className="w-4 h-4 mr-2 animate-spin" />Gemmer...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />Gem konfiguration</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="card-default border-indigo-200 bg-indigo-50/30 mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" weight="duotone" />
              <div className="text-sm text-slate-700">
                <p className="font-medium mb-1">Om planlagt import</p>
                <p>Når aktiveret, henter systemet automatisk løste Zendesk tickets og genererer KB-forslag. 
                Forslag sendes til "Zendesk Forslag" siden til godkendelse.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
